import type { PlatformStats } from './usePlatformStats'
import type { ResourceStatus } from './useLazyResource'

/**
 * The four tiles every platform-counter surface shows, in this fixed order:
 * courts, tournaments run, rated players, matches played. Capitalized because
 * About/Solutions render the label verbatim; the home proof strip's CSS
 * uppercases its caption regardless of source case, so one label list serves
 * both. docs/ux/2026-09-23-platform-counters.md §3.1/§3.3.
 */
export const PLATFORM_STAT_LABELS = [
  'Courts',
  'Tournaments run',
  'Rated players',
  'Matches played',
] as const

/**
 * Static fallback facts, in the order the brief fixes (§3.3) — shown on error
 * or whenever any one of the four live counts is still zero (e.g. a stale
 * cache right after deploy). Never invented numbers.
 */
export const PLATFORM_STAT_FACTS = [
  'Every tournament format',
  'Bookings & members built in',
  'TrueSkill ratings',
  'Ratings update after every match',
] as const

/**
 * One ready-to-render cell. `loading`: the label is known, the count isn't —
 * consumers render a skeleton where the number would go. `value`: a real
 * count. `fact`: the whole tile is a static phrase (never paired with a
 * number or label). Every surface (home proof strip, About, Solutions) maps
 * over the same four cells and supplies its own CSS frame.
 */
export type PlatformStatCell =
  | { kind: 'loading'; label: string }
  | { kind: 'value'; label: string; value: number }
  | { kind: 'fact'; text: string }

/**
 * The one place the "all four counts must be positive, or the whole set falls
 * back to static facts" rule lives (docs/ux/2026-09-23-platform-counters.md
 * §3.3) — pure, so it can be unit-tested without rendering a hook.
 * `usePlatformStatCells` is the thin wrapper that feeds it `usePlatformStats()`.
 */
export function selectPlatformStatCells(
  status: ResourceStatus,
  data: PlatformStats | null,
): PlatformStatCell[] {
  if (status === 'idle' || status === 'loading') {
    return PLATFORM_STAT_LABELS.map((label) => ({ kind: 'loading', label }))
  }

  const hasLiveCounts =
    status === 'ready' &&
    !!data &&
    data.courts > 0 &&
    data.tournamentsCompleted > 0 &&
    data.ratedPlayers > 0 &&
    data.matchesPlayed > 0

  if (hasLiveCounts && data) {
    return [
      { kind: 'value', label: PLATFORM_STAT_LABELS[0], value: data.courts },
      { kind: 'value', label: PLATFORM_STAT_LABELS[1], value: data.tournamentsCompleted },
      { kind: 'value', label: PLATFORM_STAT_LABELS[2], value: data.ratedPlayers },
      { kind: 'value', label: PLATFORM_STAT_LABELS[3], value: data.matchesPlayed },
    ]
  }

  return PLATFORM_STAT_FACTS.map((text) => ({ kind: 'fact', text }))
}
